import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { fmtMemberId } from '@lib/member-id';
import { resultChipClasses } from '@lib/config-render';
import { fmtArrival, fmtClock, formatDuration, parseTs, turnaround } from '@lib/turnaround';
import { useReviewQueue } from '@hooks/useReviewQueue';
import { useIntakeConfig } from '@hooks/useIntakeConfig';
import { useNow } from '@hooks/useNow';
import type { IntakeConfig, SidecarRecord } from '@/types/intake';

import { useCampaign } from '@hooks/useCampaign';

type SortKey = 'arrival' | 'name' | 'result' | 'status' | 'member';

/** Editorial age color: emerald < 3d, amber 3-7d, crimson >= 7d in lab; muted when there is no USPS scan. */
function ageColor(ms: number | null): string {
  if (ms == null) return 'var(--ink-400)';
  const days = ms / 86_400_000;
  if (days >= 7) return 'var(--crimson)';
  if (days >= 3) return 'var(--amber)';
  return 'var(--emerald)';
}

/**
 * Review Turnaround - a read-only reporting module (a fifth tab after Archive). It does not change any
 * intake flow: it reads the same resulted records the Review tab shows and reports, per member, how long
 * the sample has been in the lab - a LIVE clock from the USPS lab-arrival scan (receivedAt, scan basis)
 * to now, in days / hours / minutes. Records taken in by manual override have no USPS arrival, so they
 * report "no USPS scan" and no age. Nothing here is written back; the clock recomputes every second.
 */
export function Turnaround() {
  const { campaign: CAMPAIGN } = useCampaign();
  const qc = useQueryClient();
  const cfg = useIntakeConfig(CAMPAIGN);
  const list = useReviewQueue(CAMPAIGN);
  const now = useNow();
  const [q, setQ] = useState('');
  const [fResult, setFResult] = useState('');
  const [fBasis, setFBasis] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('arrival');
  const [sortDir, setSortDir] = useState<1 | -1>(1); // arrival asc = longest in lab first

  const refresh = () => void qc.invalidateQueries({ queryKey: ['intake-review', CAMPAIGN] });

  const items = list.data?.items ?? [];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((r) => {
      if (fResult && r.result !== fResult) return false;
      if (fBasis && r.receivedBasis !== fBasis) return false;
      if (fStatus && r.status !== fStatus) return false;
      if (needle) {
        const hay = [r.memberNameSnapshot, fmtMemberId(r.memberId), r.memberId, r.kitBarcode, r.recordId]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      return true;
    });
  }, [items, q, fResult, fBasis, fStatus]);

  const sorted = useMemo(() => {
    const arr = (r: SidecarRecord) => (r.receivedBasis === 'scan' ? parseTs(r.receivedAt) : null);
    return [...filtered].sort((a, b) => {
      if (sortKey === 'arrival') {
        const va = arr(a);
        const vb = arr(b);
        if (va == null && vb == null) return 0;
        if (va == null) return 1; // no-scan records always sink to the bottom
        if (vb == null) return -1;
        return (va - vb) * sortDir;
      }
      const key =
        sortKey === 'name' ? a.memberNameSnapshot
          : sortKey === 'result' ? a.result
            : sortKey === 'status' ? a.status
              : a.memberId;
      const kb =
        sortKey === 'name' ? b.memberNameSnapshot
          : sortKey === 'result' ? b.result
            : sortKey === 'status' ? b.status
              : b.memberId;
      return String(key).localeCompare(String(kb)) * sortDir;
    });
  }, [filtered, sortKey, sortDir]);

  // Headline metrics over the full (unfiltered) resulted set.
  const stats = useMemo(() => {
    let pos = 0, neg = 0, unable = 0, scan = 0, manual = 0, noConsent = 0;
    const ages: number[] = [];
    for (const r of items) {
      if (r.result === 'Positive') pos++;
      else if (r.result === 'Negative') neg++;
      else if (r.result === 'Unable to test') unable++;
      if (r.noConsentForm) noConsent++;
      if (r.receivedBasis === 'scan') {
        scan++;
        const a = parseTs(r.receivedAt);
        if (a != null) ages.push(Math.max(0, now - a));
      } else manual++;
    }
    ages.sort((a, b) => a - b);
    let median: number | null = null;
    if (ages.length) {
      const mid = Math.floor(ages.length / 2);
      median = ages.length % 2 ? ages[mid]! : Math.round((ages[mid - 1]! + ages[mid]!) / 2);
    }
    return { total: items.length, pos, neg, unable, scan, manual, noConsent, median };
  }, [items, now]);

  const setSort = (k: SortKey) => {
    if (sortKey === k) setSortDir((d) => (d === 1 ? -1 : 1));
    else {
      setSortKey(k);
      setSortDir(1);
    }
  };

  if (cfg.isLoading || !cfg.data)
    return (
      <div className="li-page li-page--wide">
        <div className="li-state">Loading...</div>
      </div>
    );
  const config = cfg.data;

  const tiles: { label: string; value: string | number; color: string }[] = [
    { label: 'Samples', value: stats.total, color: 'var(--ink-950)' },
    { label: 'Negative', value: stats.neg, color: 'var(--emerald)' },
    { label: 'Positive', value: stats.pos, color: 'var(--crimson)' },
    { label: 'Unable', value: stats.unable, color: '#15619E' },
    { label: 'USPS scan', value: stats.scan, color: 'var(--ink-950)' },
    { label: 'Manual', value: stats.manual, color: 'var(--ink-400)' },
    { label: 'Missing consent', value: stats.noConsent, color: 'var(--amber)' },
    {
      label: 'Median in lab',
      value: stats.median == null ? '-' : formatDuration(stats.median),
      color: ageColor(stats.median),
    },
  ];

  return (
    <div className="li-page li-page--wide">
      <div className="li-head flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="section-eyebrow">Reporting - 26 FOBT</div>
          <h2 className="section-title">Review <em>Turnaround</em></h2>
          <p className="mt-1 max-w-2xl text-xs" style={{ color: 'var(--ink-400)', lineHeight: 1.5 }}>
            Reporting only - reads the resulted records and shows, per member, the live time in the lab from
            USPS arrival to now. Records taken in by manual override have no USPS scan. Nothing here changes
            the intake workflow.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="micro" style={{ marginBottom: 2 }}>Now</div>
            <div className="font-mono text-xs tab-num" style={{ color: 'var(--ink-700)' }}>{fmtClock(now)}</div>
          </div>
          <button
            type="button"
            onClick={refresh}
            disabled={list.isFetching}
            className="btn btn-ghost btn-sm disabled:opacity-50"
          >
            {list.isFetching ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Stats strip. */}
      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
        {tiles.map((s) => (
          <div key={s.label} className="stat" style={{ padding: '10px 12px' }}>
            <div className="value tab-num" style={{ fontSize: 20, color: s.color }}>
              {s.value}
            </div>
            <div className="label" style={{ marginBottom: 0, marginTop: 2 }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>

      {/* Filters. */}
      <div className="li-toolbar">
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Filter by name, member ID, kit, or record ID"
          className="text-input grow"
          style={{ minWidth: '14rem' }}
        />
        <select value={fResult} onChange={(e) => setFResult(e.target.value)} className="select-input" style={{ width: 'auto' }}>
          <option value="">All results</option>
          <option>Negative</option>
          <option>Positive</option>
          <option>Unable to test</option>
        </select>
        <select value={fBasis} onChange={(e) => setFBasis(e.target.value)} className="select-input" style={{ width: 'auto' }}>
          <option value="">All arrivals</option>
          <option value="scan">USPS scan</option>
          <option value="override">Manual (no scan)</option>
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} className="select-input" style={{ width: 'auto' }}>
          <option value="">All statuses</option>
          <option value="submitted">submitted</option>
          <option value="returned">returned</option>
          <option value="approved">approved</option>
          <option value="applied">applied</option>
        </select>
        <span className="ml-auto text-xs tab-num" style={{ color: 'var(--ink-400)' }}>
          {sorted.length} of {items.length} shown
        </span>
      </div>

      {list.isLoading && <p className="text-sm" style={{ color: 'var(--ink-400)' }}>Loading records...</p>}
      {!list.isLoading && items.length === 0 && (
        <div className="li-state">No resulted records yet. This report fills as samples are resulted.</div>
      )}

      {items.length > 0 && (
        <div className="card card-flush overflow-hidden">
          <div className="overflow-x-auto">
            <table className="dt">
              <thead>
                <tr>
                  <th>#</th>
                  <Th label="Member" k="name" sortKey={sortKey} sortDir={sortDir} onSort={setSort} />
                  <th>Member ID</th>
                  <th>Kit</th>
                  <Th label="Result" k="result" sortKey={sortKey} sortDir={sortDir} onSort={setSort} />
                  <Th label="Status" k="status" sortKey={sortKey} sortDir={sortDir} onSort={setSort} />
                  <Th label="Arrived (USPS)" k="arrival" sortKey={sortKey} sortDir={sortDir} onSort={setSort} />
                  <th>In lab (live)</th>
                  <th>Processed in</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, i) => (
                  <Row key={r.recordId} record={r} index={i} now={now} config={config} />
                ))}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={9} className="text-center text-sm" style={{ color: 'var(--ink-400)', padding: '24px' }}>
                      No records match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

/** A sortable header cell. */
function Th({
  label,
  k,
  sortKey,
  sortDir,
  onSort,
}: {
  label: string;
  k: SortKey;
  sortKey: SortKey;
  sortDir: 1 | -1;
  onSort: (k: SortKey) => void;
}) {
  const active = sortKey === k;
  return (
    <th>
      <button
        type="button"
        onClick={() => onSort(k)}
        className="inline-flex items-center gap-1"
        style={{ color: active ? 'var(--ink-900)' : 'var(--ink-400)', textTransform: 'inherit', letterSpacing: 'inherit', font: 'inherit' }}
      >
        {label}
        <span className="text-[10px]">{active ? (sortDir === 1 ? '↑' : '↓') : '↕'}</span>
      </button>
    </th>
  );
}

function Row({
  record,
  index,
  now,
  config,
}: {
  record: SidecarRecord;
  index: number;
  now: number;
  config: IntakeConfig;
}) {
  const arrivalTs = record.receivedBasis === 'scan' ? record.receivedAt : null;
  const ta = turnaround(arrivalTs, now);
  const resultedMs = parseTs(record.resultedAt);
  const processed = arrivalTs && resultedMs != null ? turnaround(arrivalTs, resultedMs) : null;

  return (
    <tr>
      <td>
        <span
          className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold tab-num"
          style={{ background: 'var(--ink-900)', color: 'var(--paper)' }}
        >
          {index + 1}
        </span>
      </td>
      <td className="font-medium text-cdx-fg-strong">
        {record.memberNameSnapshot || '(no name)'}
        {record.noConsentForm && <span className="pill pill-warning ml-2">no consent</span>}
      </td>
      <td className="li-mono">{fmtMemberId(record.memberId)}</td>
      <td className="li-mono">{record.kitBarcode}</td>
      <td>
        <span className={resultChipClasses(config, record.result)}>{record.result}</span>
      </td>
      <td className="text-xs" style={{ color: 'var(--ink-600)' }}>{record.status}</td>
      <td className="text-xs" style={{ color: 'var(--ink-600)' }}>
        {arrivalTs ? fmtArrival(arrivalTs) : <span style={{ color: 'var(--ink-400)' }}>manual (no scan)</span>}
      </td>
      <td className="font-semibold tab-num" style={{ color: ageColor(ta ? ta.ms : null) }}>
        {ta ? ta.text : 'no USPS scan'}
      </td>
      <td className="text-xs tab-num" style={{ color: 'var(--ink-400)' }}>{processed ? processed.text : '-'}</td>
    </tr>
  );
}
